import type { DemoLocationSummary, DemoProfile } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@lms/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { useToast } from '@lms/ui/use-toast';
import { RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { useState, useTransition } from 'react';
import { useTranslations } from 'use-intl';
import { DemoConfirmDialog } from '@/features/demo/confirm-dialog';
import {
  disableDemoAction,
  enableDemoAction,
  refreshDemoAction,
} from '@/features/demo/demo-api';

const PROFILE_LABEL = {
  busy: 'profile_busy',
  'new-location': 'profile_new_location',
  seasonal: 'profile_seasonal',
} as const;

const PROFILES = Object.keys(PROFILE_LABEL) as DemoProfile[];

export function DemoControls({
  locationId,
  current,
  autoRefreshEnabled,
  retentionDays,
  onChanged,
}: {
  locationId: number;
  current: DemoLocationSummary | undefined;
  autoRefreshEnabled: boolean;
  retentionDays: number;
  onChanged: () => void;
}) {
  const t = useTranslations('demo');
  const { toast } = useToast();
  const [pending, startTransition] = useTransition();
  const [profile, setProfile] = useState<DemoProfile>('busy');
  const [confirming, setConfirming] = useState<'fill' | 'disable' | null>(null);

  const isDemo = current !== undefined;

  // The Next server actions revalidated the page; here a successful action
  // revalidates the shared demo status via SWR `mutate` (onChanged).
  const run = (fn: () => Promise<{ ok: boolean; error?: string }>) =>
    startTransition(async () => {
      const result = await fn();
      if (result.ok) {
        onChanged();
      } else {
        toast({
          title: t('error'),
          description: result.error,
          variant: 'destructive',
        });
      }
    });

  const onEnable = () =>
    run(async () => {
      const r = await enableDemoAction(locationId, profile);
      if (r.ok) {
        toast({
          title: t('filled_title'),
          description: t('filled_description', {
            students: r.students.toLocaleString(),
            sessions: r.sessions.toLocaleString(),
          }),
        });
      }
      return r;
    });

  const onRefresh = () =>
    run(async () => {
      const r = await refreshDemoAction();
      if (r.ok) {
        toast({ title: t('refreshed_title') });
      }
      return r;
    });

  const onDisable = () =>
    run(async () => {
      const r = await disableDemoAction(locationId);
      if (r.ok) {
        toast({ title: t('disabled_title') });
      }
      return r;
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          {t('title')}
          {isDemo && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
              {t('on')}
            </span>
          )}
        </CardTitle>
        <CardDescription>{t('card_description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isDemo ? (
          <p className="text-muted-foreground text-sm">
            {t('summary', {
              students: current.students.toLocaleString(),
              sessions: current.sessions.toLocaleString(),
              groups: current.groups.toLocaleString(),
            })}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">{t('not_on')}</p>
        )}

        {!autoRefreshEnabled && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800 text-sm dark:bg-amber-950 dark:text-amber-200">
            {t('auto_refresh_off')}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={profile}
            onValueChange={(v) => setProfile(v as DemoProfile)}
            disabled={pending}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROFILES.map((p) => (
                <SelectItem key={p} value={p}>
                  {t(PROFILE_LABEL[p])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={() => setConfirming('fill')} disabled={pending}>
            <Sparkles className="h-4 w-4" />
            {isDemo ? t('refill') : t('enable')}
          </Button>

          {isDemo && (
            <>
              <Button variant="outline" onClick={onRefresh} disabled={pending}>
                <RefreshCw className="h-4 w-4" />
                {t('refresh')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setConfirming('disable')}
                disabled={pending}
              >
                <Trash2 className="h-4 w-4" />
                {t('disable')}
              </Button>
            </>
          )}
        </div>

        <p className="text-muted-foreground text-xs">
          {t('retention_note', { days: retentionDays })}
        </p>
      </CardContent>

      <DemoConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirming(null);
          }
        }}
        title={
          confirming === 'disable'
            ? t('confirm_disable_title')
            : t('confirm_fill_title')
        }
        description={
          confirming === 'disable'
            ? t('confirm_disable_description')
            : t('confirm_fill_description')
        }
        confirmLabel={
          confirming === 'disable'
            ? t('disable')
            : isDemo
              ? t('refill')
              : t('enable')
        }
        pending={pending}
        onConfirm={() => {
          const action = confirming;
          setConfirming(null);
          if (action === 'disable') {
            onDisable();
          } else {
            onEnable();
          }
        }}
      />
    </Card>
  );
}
